import { createFileRoute } from "@tanstack/react-router";
import { CategoryLanding, categoryHead, productsQueryOptions } from "@/components/CategoryLanding";

const SLUG = "oppimistorni";
const H1 = "Oppimistorni & seisontajakkara — turvallinen apujakkara lapselle keittiöön";
// Title is the primary landing for "seisontajakkara" (110/mo). Keep the
// keyword early — three product URLs currently split the ranking and
// this page should out-signal all of them for the term.
const TITLE = "Oppimistorni & seisontajakkara lapselle | Montessori24";
const DESC =
  "Turvallinen seisontajakkara lapselle keittiöön: oppimistorni suojakaiteilla, säädettävä askelma, koivuvaneri. Ilmainen toimitus Suomeen 3–7 pv.";

const FAQS = [
  {
    q: "Mikä on oppimistorni?",
    a: "Oppimistorni (learning tower, taaperotorni) on korkea, kaiteilla suojattu apujakkara, jonka avulla lapsi yltää keittiön työtasolle turvallisesti. Montessori-pedagogiikassa se tukee lapsen itsenäisyyttä: hän voi osallistua ruoanlaittoon ja arjen puuhiin aikuisen rinnalla.",
  },
  {
    q: "Minkä ikäiselle oppimistorni sopii?",
    a: "Oppimistornien ikäsuositukset vaihtelevat mallin ja valmistajan mukaan — tarkista aina tuotekohtainen ikäsuositus tuotesivulta. Lapsen tulee osata seistä tukevasti itsenäisesti. Askelmien korkeutta voi säätää lapsen kasvun mukaan monessa mallissa, joten sama taaperotorni palvelee vuosia.",
  },
  {
    q: "Oppimistorni vai apujakkara lapselle?",
    a: "Tavallinen apujakkara on matala eikä sisällä kaiteita, joten se sopii vasta 4–5-vuotiaalle. Oppimistorni eli taaperotorni nostaa lapsen työtasolle ja suojaa kaikilta neljältä sivulta, joten 1–3-vuotias lapsi ei putoa vaikka horjahtaisi.",
  },
  {
    q: "Onko seisontajakkara sama asia kuin oppimistorni?",
    a: "Seisontajakkara on yleisnimi kaikelle korokkeelle, jolla lapsi seisoo aikuisen työtason korkeudella — myös oppimistornille. Turvallisin seisontajakkara taaperolle on nimenomaan oppimistorni, koska kaiteet estävät sivulle putoamisen.",
  },
  {
    q: "Onko oppimistorni turvallinen?",
    a: "Kyllä, kun torni on vakaa (leveä jalusta) ja lapsi käyttää sitä valvotusti. Tarkista tuotekohtainen ikäsuositus, enimmäiskuormitus ja turvallisuustiedot tuotteen omalta sivulta.",
  },
  {
    q: "Toimiiko oppimistorni myös liitutauluna tai piirustuspöytänä?",
    a: "Monissa malleissamme yläpuoli tai etuseinä toimii liitutauluna ja jotkin kääntyvät tuoliksi + pieneksi pöydäksi. Näin torni palvelee pidempään lapsen kasvaessa.",
  },
];

export const Route = createFileRoute("/oppimistorni")({
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: OppimistorniPage,
});

function OppimistorniPage() {
  return (
    <CategoryLanding
      slug={SLUG}
      eyebrow="Oppimistorni"
      title={H1}
      intro="Oppimistorni — tunnetaan myös nimellä taaperotorni, learning tower tai turvallinen apujakkara lapselle — nostaa taaperon keittiön työtasolle. Vakaa jalusta, suojaavat kaiteet ja säädettävä askelmakorkeus tekevät siitä Montessori-arjen ykköstyökalun 1–5-vuotiaille. Käytännössä paras seisontajakkara-vaihtoehto pienelle kokkiapulaiselle."
      kauppaCategory="tower"
      match={(t) => /torni|apujakkar|jakkar|helper tower|kitchen tower|tower|stool/i.test(t)}
      relatedChips={[
        { label: "Kaikki tuotteet", to: "/kauppa" },
        { label: "Kiipeilytelineet", to: "/kiipeilyteline" },
        { label: "Pikler-kolmio", to: "/pikler-kolmio" },
        { label: "Oppimistorni-opas", to: "/oppimistorni-opas" },
      ]}
      productChips={[
        { label: "Oppimistorni liitutaululla", handle: "montessori-helper-tower-step-stool-kitchen-tower-with-blackboard" },
        { label: "Oppimistorni apujakkara", handle: "montessori-helper-tower-step-stool" },
        { label: "2-in-1 oppimistorni + pöytä", handle: "2in1-kitchen-tower-table-chair" },
      ]}
      body={[
        {
          heading: "Miksi oppimistorni kuuluu jokaiseen lapsiperheen keittiöön",
          paragraphs: [
            "Lapsi oppii tekemällä. Kun taapero pääsee omalle korkeudelleen keittiön työtasolle turvallisen oppimistornin avulla, hän voi pestä porkkanoita, sekoittaa taikinaa, kaataa vettä ja osallistua ruoanlaittoon aikuisen rinnalla. Nämä pienet arjen tehtävät kehittävät hienomotoriikkaa, keskittymiskykyä ja itseluottamusta enemmän kuin mikään yksittäinen lelu.",
            "Toisin kuin tavallinen apujakkara tai jakkara, oppimistorni suojaa lasta kaikilta neljältä sivulta. Vanhempi voi keskittyä ruoanlaittoon tietäen, ettei lapsi putoa vaikka horjahtaisi. Sama taaperotorni palvelee vuosia — monessa mallissa askelmakorkeus säätyy lapsen kasvaessa 1-vuotiaasta lähes kouluikään.",
          ],
        },
        {
          heading: "Apujakkara lapselle vai oppimistorni — kumpi kannattaa valita?",
          paragraphs: [
            "Perinteinen apujakkara lapselle on matala koroke ilman kaiteita: lapsi nousee sen päälle, mutta yhtään horjahdusta se ei anna anteeksi. Siksi tavallinen apujakkara sopii vasta 4–5-vuotiaalle, joka seisoo jo tukevasti aikuisen tapaan — tunnettu ratkaisu on esimerkiksi Ikean pikku apulainen.",
            "Oppimistorni on käytännössä apujakkara + kaiteet neljältä sivulta. Sillä 1–3-vuotias pääsee turvallisesti tiskialtaan tai työtason korkeudelle: kaide estää sivulle putoamisen ja etuseinän aukko on mitoitettu lapsen kokoiseksi. Käyttöikä on 3–5× pidempi kuin apujakkaralla, joten yksi taaperotorni korvaa useamman jakkarahankinnan.",
          ],
        },
        {
          heading: "Seisontajakkara keittiöön — turvallisin vaihtoehto taaperolle",
          paragraphs: [
            "Seisontajakkara on suomalaisessa arjessa käytetty nimi kaikelle koroketuolista tikkaisiin, jolla lapsi nousee aikuisen työtason tasalle. Ongelma: ilman kaidetta 1–3-vuotias horjahtaa helposti ja pudotus 45 cm korkeudesta betonilattialle on tosissaan otettava riski.",
            "Oppimistorni on turvallisin seisontajakkara taaperolle: leveä jalusta pitää sen paikallaan, kaiteet suojaavat kaikilta sivuilta ja säädettävä askelma pitenee lapsen kasvun mukana. Yksi hankinta korvaa jakkaran, tikkaat ja tuolikoroketuolin — ja on selvästi turvallisempi kuin perinteinen apujakkara lapselle.",
          ],
        },
        {
          heading: "Mihin kiinnittää huomiota oppimistornia tai taaperotornia valitessa",
          paragraphs: [
            "Vakaus on tärkein: leveä jalusta ja matala painopiste estävät kaatumisen. Katso, että kaiteet ovat riittävän korkeat ja etuseinän aukko lapsen kokoinen — liian väljä avanne on turvallisuusriski aivan pienelle.",
            "Materiaali kannattaa tarkistaa. Valikoimassamme käytetään pääosin koivuvaneria ja vesiohenteisia pintakäsittelyjä. Tuotekohtaiset materiaalitiedot löydät tuotesivulta.",
            "Bonuksena kannattaa etsiä säädettävä askelma ja monikäyttöisyys — esimerkiksi liitutaulu-etuseinä tai muunneltavuus tuoli-pöytä-yhdistelmäksi pidentävät käyttöikää useilla vuosilla.",
          ],
        },
      ]}
      faqs={FAQS}
    />
  );
}